


import { useState, useRef, useEffect } from 'react'

interface Props {
    todaySales: number
    target: number
    targetMode: 'lastYearWeeklyAvg' | 'fixed' | string
    targetMultiplier?: number
    isPastDate?: boolean
}

export default function DailyPerformanceCountdown({ todaySales, target, targetMode, targetMultiplier, isPastDate }: Props) {
    const diff = todaySales - target
    const isHigher = diff >= 0
    const absDiff = Math.abs(diff)
    const [showTooltip, setShowTooltip] = useState(false)
    const tooltipRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (tooltipRef.current && !tooltipRef.current.contains(event.target as Node)) {
                setShowTooltip(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    const getExplainerText = () => {
        if (targetMode === 'fixed') {
            return "This target is a fixed daily amount configured by an administrator."
        }
        
        let adjustmentStr = ''
        if (targetMultiplier) {
            const sign = targetMultiplier > 0 ? '+' : ''
            adjustmentStr = ` (${sign}${targetMultiplier}%)`
        }
        
        if (targetMode === 'lastYearWeeklyCumulative') {
            return `This target is calculated dynamically to achieve the weekly goal. It takes the total last year's weekly sales${adjustmentStr}, subtracts the sales achieved so far this week, and divides the remainder evenly across the remaining days.`
        }
        
        return `This target is calculated based on last year's average daily sales for this week, multiplied by the configured adjustment${adjustmentStr}.`
    }

    // Formatter for currency
    const formatCurrency = (val: number) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(val)
    }

    // Determine colors and text based on achievement
    const textColor = isHigher ? 'var(--color-success)' : 'var(--color-warning)'
    const subText = isHigher ? 'over target!' : (isPastDate ? 'under target' : 'to go')
    const containerBg = isHigher ? 'linear-gradient(135deg, rgba(34, 197, 94, 0.1) 0%, rgba(34, 197, 94, 0.02) 100%)' : 'var(--color-surface)'
    const containerBorder = isHigher ? '1px solid rgba(34, 197, 94, 0.3)' : '1px solid var(--color-border)'

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            padding: '1.5rem',
            background: containerBg,
            border: containerBorder,
            borderRadius: 'var(--radius-lg)',
            boxShadow: 'var(--shadow-sm)',
            transition: 'all 0.3s ease',
            textAlign: 'center'
        }}>
            <h3 style={{
                fontSize: 'var(--text-sm)',
                color: 'var(--color-text-secondary)',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                marginBottom: '1rem',
                margin: 0
            }}>
                Daily Performance Target
            </h3>
            
            <div style={{
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'center',
                flexWrap: 'wrap',
                gap: '8px',
                marginTop: '1rem',
                marginBottom: '1.5rem'
            }}>
                <span style={{
                    fontSize: 'clamp(2rem, 5vw, 4rem)', // XXXL font
                    fontWeight: 800,
                    color: textColor,
                    lineHeight: 1,
                    letterSpacing: '-0.02em',
                    textShadow: isHigher ? '0 0 20px rgba(34, 197, 94, 0.2)' : 'none'
                }}>
                    {formatCurrency(absDiff)}
                </span>
                <span style={{
                    fontSize: 'var(--text-lg)',
                    fontWeight: 600,
                    color: isHigher ? 'var(--color-success)' : 'var(--color-text-tertiary)',
                    opacity: 0.9
                }}>
                    {subText}
                </span>
            </div>

            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '1.5rem',
                marginTop: 'auto',
                paddingTop: '1rem',
                borderTop: '1px solid var(--color-border)',
                width: '100%'
            }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>
                        Today's Sales
                    </span>
                    <span style={{ fontSize: 'var(--text-md)', fontWeight: 600, color: 'var(--color-text)' }}>
                        {formatCurrency(todaySales)}
                    </span>
                </div>
                <div style={{ width: '1px', height: '24px', background: 'var(--color-border)' }} />
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px' }}>
                        <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            Daily Target
                        </span>
                        <div ref={tooltipRef} style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                            <button 
                                onClick={(e) => { e.stopPropagation(); setShowTooltip(!showTooltip); }}
                                style={{ 
                                    background: 'none', padding: 0, cursor: 'pointer', 
                                    color: 'var(--color-text-tertiary)', fontSize: '10px', 
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    width: '14px', height: '14px', borderRadius: '50%', border: '1px solid var(--color-border)'
                                }}
                                title="Click for target details"
                            >
                                i
                            </button>
                            {showTooltip && (
                                <div style={{
                                    position: 'absolute', bottom: '100%', left: '50%', transform: 'translateX(-50%)',
                                    marginBottom: '8px', padding: '8px 12px', background: 'var(--color-surface)',
                                    border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)',
                                    boxShadow: 'var(--shadow-md)', color: 'var(--color-text)', fontSize: '12px',
                                    width: '200px', zIndex: 10, textAlign: 'left', textTransform: 'none',
                                    letterSpacing: 'normal'
                                }}>
                                    {getExplainerText()}
                                </div>
                            )}
                        </div>
                    </div>
                    <span style={{ fontSize: 'var(--text-md)', fontWeight: 600, color: 'var(--color-text-secondary)' }}>
                        {formatCurrency(target)}
                    </span>
                </div>
            </div>
        </div>
    )
}
